import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getAsset } from '../../utils/getAsset';
import EmptyState from '../EmptyState';
import NftImage from '../NftImage';

const AssetSelector = ({ assets = [], selectedAssets = [], onToggleAsset, isLoading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const selectorRef = useRef(null);
  const itemsPerPage = 24;

  const assetDetailsQueries = useQuery({
    queryKey: [
      'allAssetDetails',
      assets
        .map((a) => a['asset-id'])
        .sort()
        .join(','),
    ],
    queryFn: async () => {
      if (assets.length === 0) return [];

      const assetDetails = await Promise.all(
        assets.map(async (asset) => {
          try {
            const details = await getAsset(asset['asset-id']);
            return {
              ...asset,
              params: details?.params,
              name: details?.params?.name || `Asset ${asset['asset-id']}`,
            };
          } catch (error) {
            return {
              ...asset,
              name: `Asset ${asset['asset-id']}`,
            };
          }
        })
      );
      return assetDetails;
    },
    enabled: assets.length > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });

  const assetsWithDetails = assetDetailsQueries.data || assets;

  const filteredAssets = useMemo(() => {
    if (!searchQuery) return assetsWithDetails;

    return assetsWithDetails.filter((asset) => {
      const name = asset.name || asset.params?.name || `Asset ${asset['asset-id']}`;
      return name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [assetsWithDetails, searchQuery]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAssets.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const scrollToTop = () => {
    if (selectorRef.current) {
      selectorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      scrollToTop();
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      scrollToTop();
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    scrollToTop();
  };

  if (isLoading) {
    return (
      <div className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6'>
        {Array.from({ length: 12 }).map((_, index) => (
          <div key={index} className='aspect-square bg-gray-900 rounded-lg animate-pulse' />
        ))}
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className='mt-16 flex justify-center'>
        <EmptyState message='You do not have any NFTs available for donation.' />
      </div>
    );
  }

  return (
    <div className='relative' ref={selectorRef}>
      <div className='mb-6'>
        <input
          type='text'
          placeholder='Search NFTs...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
        />
        {assetDetailsQueries.isLoading && searchQuery && <div className='mt-2 text-xs text-gray-400'>Loading asset details for search...</div>}
      </div>

      <div className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6'>
        {currentItems.map((asset) => (
          <AssetSelectorItem
            key={asset['asset-id']}
            asset={asset}
            assetName={asset.name}
            isSelected={selectedAssets.some((selected) => selected['asset-id'] === asset['asset-id'])}
            onToggle={() => onToggleAsset(asset)}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className='flex flex-col items-center justify-between mt-8 space-y-4 md:flex-row md:space-y-0'>
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className='px-4 py-2 bg-gray-900 rounded-full hover:bg-gray-700 disabled:opacity-50 transition duration-250'
          >
            Previous
          </button>
          <div className='flex justify-center space-x-2'>
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              if (pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={clsx(
                      `w-10 h-10 rounded-full transition duration-250`,
                      currentPage === pageNumber ? 'bg-primary-500 text-white' : 'bg-gray-900 hover:bg-gray-700'
                    )}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                return (
                  <span key={pageNumber} className='w-10 h-10 flex items-center justify-center'>
                    ...
                  </span>
                );
              }
              return null;
            })}
          </div>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className='px-4 py-2 bg-gray-900 rounded-full hover:bg-gray-700 disabled:opacity-50 transition duration-250'
          >
            Next
          </button>
        </div>
      )}

      <div className='mt-6 text-center text-gray-400'>
        Showing {currentItems.length} of {filteredAssets.length} NFTs
        {selectedAssets.length > 0 && <span className='ml-4 text-primary-500'>{selectedAssets.length} selected for donation</span>}
      </div>
    </div>
  );
};

const AssetSelectorItem = ({ asset, assetName, isSelected, onToggle }) => {
  const getDetailsQuery = useQuery({
    queryKey: ['assetDetails', asset['asset-id']],
    queryFn: () => getAsset(asset['asset-id']),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
    enabled: !assetName,
  });

  const assetDetails = getDetailsQuery.data;
  const displayName = assetName || assetDetails?.params?.name || `Asset ${asset['asset-id']}`;

  return (
    <div>
      <button type='button' onClick={onToggle} className='relative w-full aspect-square flex flex-col items-center justify-center group'>
        {isSelected && (
          <div className='absolute top-2 right-2 z-30 w-[24px] h-[24px] flex items-center justify-center rounded-full bg-primary-500 border-2 border-white'>
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='w-3 h-3 text-white'>
              <path
                fillRule='evenodd'
                d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z'
                clipRule='evenodd'
              />
            </svg>
          </div>
        )}

        <div
          className={clsx(
            'relative z-20 w-full h-full rounded-lg bg-gray-200 border-4 transition-all duration-250 group-hover:border-primary-500',
            isSelected ? 'border-primary-500' : 'border-transparent'
          )}
        >
          <div className='relative z-20 aspect-square !rounded-none'>
            <NftImage nft={asset} className={clsx('overflow-hidden rounded-md absolute inset-0 w-full h-full')} />
          </div>
        </div>
        <div className='mt-2 text-xs text-gray-300 text-center truncate px-1'>{displayName}</div>
      </button>
    </div>
  );
};

export default AssetSelector;
